<template>
    <div>
        <hts-slide-over
            :show="props.show"
            :title="$t('labels.supplyInsurance.title')"
            width="50%"
            @close="closeOverSide">
            <template #body>
                <hts-card>
                    <template #toolbar>
                        <div class="w-full text-right">
                            <hts-form-button
                                v-if="group.isAdmin || permissions.includes('supply_insurance_add')"
                                type="submit"
                                icon-name="hi-plus-circle-icon"
                                icon-position="right"
                                @click="onCreateSupplyInsurance">
                                {{ $t('labels.add') }}
                            </hts-form-button>
                        </div>
                    </template>
                    <div>
                        <hts-data-table
                            ref="tableSupplyInsurance"
                            :headers="supplyPluginHeader"
                            :server="getApi"
                            :get-context-menu-items="getContextMenuItems" />
                    </div>
                </hts-card>
            </template>
            <template #footer>
                <div />
            </template>
        </hts-slide-over>
        <supply-insurance-editor
            v-if="showInsuranceEditor"
            :show="showInsuranceEditor"
            :action="supplyInsurance"
            :insurance-data="insuranceSelected"
            @close="showInsuranceEditor = false"
            @on-submit="onSubmit" />
        <hts-modal
            :show="showModalDelete"
            :title="$t('labels.deleteSupplyInsurance')"
            type="confirm"
            :content="$t('labels.confirmDeleteSupplyInsurance')"
            @hide="onCloseModal"
            @confirm="() => onDelete(idsDelete)" />
    </div>
</template>
<script lang="ts" setup>
import { ISupplyInsurance } from '../models/supply'
import SupplyInsuranceEditor from './supply-insurance-editor.vue'

const { $formatters } = useNuxtApp()
const store = $store()
const group = store.system.getGroup
const permissions = ref<Array<String>>([])
const showModalDelete = ref(false)
const idsDelete = ref<string[]>([])

const onConfirmDelete = (ids: string[]) => {
  showModalDelete.value = true
  idsDelete.value = ids
}

const onCloseModal = () => {
  showModalDelete.value = false
}
onMounted(() => {
  permissions.value = group.permissions
})
const emits = defineEmits(['close'])
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
    required: true
  },
  id: {
    type: String,
    default: '',
    required: true
  }
})

const supplyPluginHeader = [
  {
    field: 'vendor',
    headerName: $t('labels.supplyList.vendor'),
    minWidth: 200,
    sortable: true
  },
  {
    field: 'amount',
    headerName: $t('labels.amount'),
    minWidth: 200,
    sortable: true
  },
  {
    field: 'expiredDate',
    headerName: $t('labels.expiredDate'),
    minWidth: 200,
    valueGetter: (params: any) => {
      return params.data.expiredDate ? $formatters.datetime(params.data.expiredDate) : 'N.A'
    },
    sortable: true
  },
  {
    field: 'description',
    headerName: $t('labels.description'),
    minWidth: 200,
    sortable: true
  }
]

const supplyInsuranceDefault: ISupplyInsurance = {
  ID: '',
  supply: '',
  amount: 0,
  description: '',
  expiredDate: '',
  vendor: ''
}

const insuranceSelected: ISupplyInsurance = reactive({ ...supplyInsuranceDefault })

const supplyInsurance = ref('create')
const showInsuranceEditor = ref(false)
const tableSupplyInsurance = ref()

const getId = computed(() => props.id)
const getApi = computed(() => {
  const url = $url.supply.insurance.supplyInsuranceListByID
  const temp = Object.assign({}, url)
  temp.url = temp.url.replace('ID', props.id)
  return temp
})

const closeOverSide = () => {
  emits('close')
}

const onCreateSupplyInsurance = () => {
  supplyInsurance.value = 'create'
  showInsuranceEditor.value = true
}
function _formatDate (date: any) {
  const d = new Date(date)
  let month = '' + (d.getMonth() + 1)
  let day = '' + d.getDate()
  const year = d.getFullYear()

  if (month.length < 2) {
    month = '0' + month
  }
  if (day.length < 2) {
    day = '0' + day
  }

  return [year, month, day].join('-')
}

const getContextMenuItems = (params: any) => {
  if (params.node && params.node.data) {
    const menuItem = [
      group.isAdmin || permissions.value.includes('supply_insurance_edit')
        ? {
          name: $t('labels.supplyInsurance.edit'),
          action: () => {
            insuranceSelected.ID = params.node.data.id
            insuranceSelected.supply = params.node.data.supply
            insuranceSelected.amount = params.node.data.amount
            insuranceSelected.description = params.node.data.description
            insuranceSelected.expiredDate = _formatDate(params.node.data.expiredDate)
            insuranceSelected.vendor = params.node.data.vendor
            supplyInsurance.value = 'edit'
            showInsuranceEditor.value = true
          }
        }
        : null,
      group.isAdmin || permissions.value.includes('supply_insurance_delete')
        ? {
          name: $t('labels.supplyInsurance.delete'),
          action: () => {
            const ids = [params.node.data.id]
            onConfirmDelete(ids)
          }
        }
        : null
    ]
    return menuItem
  }
  return []
}
const onSubmit = $lodash.debounce(async (data: any, action: string) => {
  const url = action === 'create' ? $url.supply.insurance.supplyInsuranceCreate : $url.supply.insurance.supplyInsuranceUpdate
  if (action === 'create') {
    data.supply = getId.value
  }
  const result = await $api(url, { body: new URLSearchParams(data) })
  const success = $lodash.get(result, 'data.value.success')
  const error = $lodash.get(result, 'data.value.data')
  if (success) {
    if (action === 'create') {
      $toast().success($t('labels.createSussess'))
    }
    if (action === 'edit') {
      $toast().success($t('labels.updateSuccess'))
    }
    tableSupplyInsurance.value.refresh()
    showInsuranceEditor.value = false
  } else {
    process.client && $toast().error(error.errors[0].msg)
  }
}, 500)
const onDelete = $lodash.debounce(async (ids: String[]) => {
  const url = $url.supply.insurance.supplyInsuranceDelete
  const result = await $api(url, { body: { ids } })
  const success = $lodash.get(result, 'data.value.success')
  const error = $lodash.get(result, 'data.value.data')
  if (success) {
    $toast().success($t('labels.deleteSuccess'))
    tableSupplyInsurance.value.refresh()
    onCloseModal()
  } else {
    process.client && $toast().error(error.errors[0].msg)
  }
})
</script>
